import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Progress, Button, Header } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as checklistActions from '../../actions/checklistActions';
import {
  MotifButton,
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifFileUploader,
  MotifMessage,
} from '@ey-xd/motif-react';
import * as Constants from '../../constants/other';
import * as FileUploadValidations from '../../common/FileUploadValidations';

/**
 * Container component used for uploading trial balance files
 */
export class ChecklistFileUploadModal extends React.Component {
  /**
   * Creates a new ChecklistFileUploadModal
   * @constructor
   * @param {Object} props The component properties
   * @param {Object} context The component context
   */
  constructor(props, context) {
    super(props, context);

    this.renderUploadStatus = this.renderUploadStatus.bind(this);
    this.handleOnDropPostValidation =
      this.handleOnDropPostValidation.bind(this);
    this.onDrop = this.onDrop.bind(this);

    this.state = {
      status: null,
      rejectedFiles: [],
    };

    this.acceptedFileExtensions = '.xlsx,.docx,.pdf';
    const extensions = process.env.WHITELISTED_EXTENSIONS;
    if (Array.isArray(extensions)) {
      this.acceptedFileExtensions = extensions.join();
    }
  }

  /// <summary>
  /// Handles onDrop event after validation of Report Period Visibility Constraint.
  /// </summary>
  handleOnDropPostValidation = (acceptedFiles, rejectedFiles) => {
    if (Array.isArray(rejectedFiles) && rejectedFiles.length > 0) {
      this.setState({ status: 'Rejected', rejectedFiles });
    } else if (Array.isArray(acceptedFiles)) {
      const activeChecklist = this.props.isArqc
        ? this.props.arqcActiveChecklist
        : this.props.psqActiveChecklist;
      if (activeChecklist) {
        this.setState({ status: 'Uploading', rejectedFiles: [] });

        this.props.checklistActions
          .uploadChecklistFiles(
            acceptedFiles,
            activeChecklist.checklistID,
            this.props.isArqc ? 'ARQC' : 'PSQ',
            this.props.questionId,
            this.props.checklistId,
            this.props.isExternal
          )
          .then((responses) => {
            if (responses.every((r) => r)) {
              this.refreshChecklistFiles(
                activeChecklist.checklistID,
                this.props.isArqc ? 'ARQC' : 'PSQ',
                this.props.checklistId
              );
              this.setState({ status: 'Success' });
            } else {
              this.setState({ status: 'Error' });
            }
          });
      }
    }
  };

  onDrop(acceptedFiles, rejectedFiles = []) {
    const acceptedMimeTypes = FileUploadValidations.getAcceptedMimeTypes(
      FileUploadValidations.checklistAttachmentsAcceptedTypes
    );

    // Use the  function to validate files
    const { validFiles, invalidFiles } = FileUploadValidations.validateFiles(
      acceptedFiles,
      acceptedMimeTypes
    );

    // Combine the originally rejected files with the newly rejected ones
    const combinedRejectedFiles = [...rejectedFiles, ...invalidFiles];

    // Proceed with the rest of the onDrop logic
    if (
      this.props.isExternal &&
      this.props.checkReportPeriodVisibilityConstraint != null
    ) {
      this.props.checkReportPeriodVisibilityConstraint().then((response) => {
        // This additional check to prevent any further file upload action.
        let proceedWithAction = true;

        if (
          response &&
          response.errorResponse &&
          response.errorResponse.errorCode === 'RESOURCE_NOT_FOUND'
        ) {
          proceedWithAction = false;
        }

        if (proceedWithAction) {
          this.handleOnDropPostValidation(validFiles, combinedRejectedFiles);
        }
      });
    } else {
      this.handleOnDropPostValidation(validFiles, combinedRejectedFiles);
    }
  }

  refreshChecklistFiles(checklistId, checklistType, checklistGuid) {
    this.props.checklistActions.fetchAttachmentDetail(
      this.props.client.id,
      checklistId,
      null,
      checklistGuid,
      checklistType
    );
    const request = {
      GetCountOfAttachments: true,
      GetCountOfDiscrepencies: true,
      GetCountOfComments: true,
    };

    this.props.checklistActions.fetchChecklistNavCounts(
      this.props.client.id,
      checklistId,
      request,
      checklistGuid,
      checklistType
    );
  }
  renderUploadStatus() {
    let msg;
    let colorClass;

    if (this.state.status == 'Uploading') {
      msg = 'Uploading file(s)';
    } else if (this.state.status == 'Success') {
      msg = 'File(s) Uploaded Successfully';
      colorClass = 'text-success';
    } else if (this.state.status == 'Rejected') {
      msg = 'The following file(s) were rejected:';
      colorClass = 'text-danger';
    } else if (this.state.status == 'Error') {
      msg = 'There was a problem uploading your file(s)';
      colorClass = 'text-danger';
    }

    return (
      msg && (
        <div className="mb-2 mr-2 ml-2">
          {this.state.status == 'Success' ? (
            <MotifMessage className="motifLabelh3 upload-success-header">
              {msg}
            </MotifMessage>
          ) : this.state.status == 'Rejected' ||
            this.state.status == 'Error' ? (
            <MotifMessage className="motifLabelh3 upload-error-header">
              {msg}
            </MotifMessage>
          ) : (
            <MotifMessage className="motifLabelh3">{msg}</MotifMessage>
          )}
          {this.state.rejectedFiles &&
            this.state.rejectedFiles.length > 0 &&
            this.state.rejectedFiles.map((f, index) => (
              <div key={index} className="row bg-danger text-white mr-2 ml-2">
                <div className="col-md-6">{f.name}</div>
                <div className="col-md-6">
                  {f.size > Constants.MAX_UPLOAD_FILE_SIZE_IN_BYTES ? (
                    <span>
                      The file size has exceeded max limit of{' '}
                      {Constants.MAX_UPLOAD_FILE_SIZE_IN_MB}
                      MB
                    </span>
                  ) : (
                    <span>Invalid file extension</span>
                  )}
                </div>
              </div>
            ))}
          {this.state.rejectedFiles &&
            this.state.rejectedFiles.length > 0 &&
            !this.state.rejectedFiles.every(
              (f) => f.size > Constants.MAX_UPLOAD_FILE_SIZE_IN_BYTES
            ) && (
              <div className="row mt-2">
                <strong className="col-md-12">
                  The following file extensions are accepted:{' '}
                  {this.acceptedFileExtensions
                    .replace(/\./g, '')
                    .replace(/\,/g, ', ')}
                </strong>
              </div>
            )}
        </div>
      )
    );
  }

  handleCloseModal() {
    this.setState({ status: null, rejectedFiles: [] });
  }

  /**
   * Render a React element
   * @returns {Object} A reference to the component
   */
  render() {
    const HeaderWrapper = ({ children }) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'start',
          justifyContent: 'center',
        }}
      >
        {children}
      </div>
    );

    const closeButtonProps = {
      title: 'Close upload file(s) to checklist question modal',
      ariaLabel: 'Close upload file(s) to checklist question modal',
      dataTestid: 'close',
    };

    return (
      <MotifModal
        variant="alt"
        show={this.props.isChecklistFileUploadModalOpen}
        onClose={() => {
          this.props.hideChecklistFileUploadModal();
          this.handleCloseModal();
        }}
        data-testid="uploadChecklistFile"
      >
        <MotifModalHeader closeButtonProps={closeButtonProps}>
          <HeaderWrapper>
            <span className="ml-2">Upload File(s) to Checklist Question</span>
          </HeaderWrapper>
        </MotifModalHeader>
        <MotifModalBody>
          <MotifFileUploader
            onDrop={this.onDrop}
            label="Drop files here or click to upload"
            maxSize={Constants.MAX_UPLOAD_FILE_SIZE_IN_BYTES}
            data-test-id="attachmentFileUpload"
            tabIndex="-1"
          />
          <div className="react-modal-content-scrollable mt-3 mb-3">
            {this.renderUploadStatus()}
          </div>
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton
            variant="secondary"
            className="right"
            type="button"
            onClick={() => {
              this.props.hideChecklistFileUploadModal();
              this.handleCloseModal();
            }}
          >
            Close
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>
    );
  }
}

ChecklistFileUploadModal.propTypes = {
  checklistId: PropTypes.string, // Will only be passed in for external users (string of email identifier)
  questionId: PropTypes.number.isRequired,
  hideChecklistFileUploadModal: PropTypes.func.isRequired,
  authHeader: PropTypes.object,
  psqActiveChecklist: PropTypes.object,
  isArqc: PropTypes.bool.isRequired,
  isChecklistFileUploadModalOpen: PropTypes.bool,
  isExternal: PropTypes.bool,
};

/**
 * Maps items from state to properties of the component
 * @param {Object} state The state
 * @param {Object} ownProps The properties of the component
 * @returns {Object} An object containing properties that the component can access
 */
function mapStateToProps(state, ownProps) {
  return {
    psqActiveChecklist: state.psqActiveChecklist,
    arqcActiveChecklist: state.arqcActiveChecklist,
    client: state.client,
  };
}

/**
 * Binds actions to the dispatcher
 * @param {Object} dispatch The action dispatcher
 * @returns {Object} An object containing properties that the component can access
 */
function mapDispatchToProps(dispatch) {
  return {
    checklistActions: bindActionCreators(checklistActions, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChecklistFileUploadModal);
